/**
 * DesignLayoutMenu.tsx - Layout Template Selection Component
 *
 * This component provides an interface for selecting and configuring layout
 * templates in the fundraiser builder with visual previews and zone information.
 *
 * Key Features:
 * - Layout template selection with visual previews
 * - Zone configuration display (percentages and arrangements)
 * - Template descriptions and use case information
 * - Real-time layout switching with preview updates
 * - Responsive template options
 * - Default section assignments for each layout
 *
 * Available Layouts:
 * - Layout 1: Full + 2/3 + 1/3 + Full (default, content-focused)
 * - Layout 2: Full + 1/3 + 2/3 + Full (reversed columns)
 * - Layout 3: Full + 1/2 + 1/2 + Full (balanced columns)
 * - Layout 4: 2/3 + 1/3 + Full (no top zone, compact)
 * - Layout 5: Full Width (single column, mobile-friendly)
 *
 * Props: None (uses MobX store and design constants)
 *
 * Dependencies: Layout templates, zone management, MobX store integration
 */

import React from "react";
import styled from "styled-components";
import { layoutTemplates } from "./designConstants";
import { handleLayoutChange } from "./DropZone";

const SubMenuContent = styled.div`
  padding: 8px 12px 12px 12px;
  background: #ffffff;
`;

const LayoutGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 8px;
  margin-bottom: 12px;
`;

const LayoutCard = styled.div<{ selected: boolean }>`
  border: 2px solid ${(props) => (props.selected ? "#3b82f6" : "#e2e8f0")};
  border-radius: 8px;
  padding: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  background: ${(props) => (props.selected ? "#f0f9ff" : "#ffffff")};
  align-items: center;
  display: flex;
  align-items: flex-start;
  gap: 12px;

  &:hover {
    border-color: #3b82f6;
    background: #f8fafc;
    transform: translateY(-1px);
    box-shadow: 0 2px 6px rgba(59, 130, 246, 0.15);
  }
`;

const LayoutImage = styled.img`
  width: 54px;
  height: auto;
  transform: scale(0.9);
  border-radius: 4px;
`;

const LayoutContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 4px;
`;

const LayoutName = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: #1e293b;
  padding-top: 5px;
`;

const LayoutDescription = styled.div`
  font-size: 16px;
  color: #64748b;
  line-height: 1.3;
`;

interface DesignLayoutMenuProps {
  selectedLayoutId: string;
  onLayoutSelect: (layout: any) => void;
}
// import { handleLayoutChange as dropZoneHandleLayoutChange } from './DropZone';

export const DesignLayoutMenu: React.FC<DesignLayoutMenuProps> = ({
  selectedLayoutId,
  onLayoutSelect,
}) => {
  // Enhanced layout selection that handles section reorganization
  const handleLayoutSelectWithReset = (layout: any) => {
    // Update the selected layout via props
    onLayoutSelect(layout);

    // Use the DropZone handleLayoutChange function which preserves section visibility
    // dropZoneHandleLayoutChange(layout.id);
  };

  return (
    <SubMenuContent>
      <LayoutGrid>
        {layoutTemplates.map((layout) => (
          <LayoutCard
            key={layout.id}
            selected={selectedLayoutId === layout.id}
            onClick={() => handleLayoutSelectWithReset(layout)}
          >
            <LayoutImage src={layout.image} alt={layout.name} />
            <LayoutContent>
              <LayoutName>{layout.name}</LayoutName>
              <LayoutDescription>{layout.description}</LayoutDescription>
            </LayoutContent>
          </LayoutCard>
        ))}
      </LayoutGrid>
    </SubMenuContent>
  );
};
